import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowUpRight, Check } from "lucide-react";

export default function MemberApply() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", date_of_birth: "",
    gender: "male", membership_type: "Player",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.date_of_birth) {
      toast.error("Please fill out all fields");
      return;
    }
    setBusy(true);
    try {
      await api.post("/applications/member", form);
      toast.success("Application submitted! An admin will share your Member ID once approved.");
      setTimeout(() => nav("/"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <div className="relative">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-24">
          <span className="pill">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))]" /> Step 01 · Join the club
          </span>
          <h1 className="mt-5 sm:mt-6 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-[1] text-balance">
            Become a <span className="font-serif-display italic text-[hsl(var(--accent))]">member</span>.
          </h1>
          <p className="mt-4 max-w-xl text-muted-foreground text-pretty">
            A short form. An admin reviews your application and shares a unique Member ID with you once approved.
          </p>

          <form className="mt-8 sm:mt-10 space-y-5" onSubmit={submit}>
            <Card className="p-5 sm:p-7 rounded-2xl space-y-5">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Personal details</div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="first_name" className="text-sm font-medium">First name</Label>
                  <Input id="first_name" data-testid="apply-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-sm font-medium">Last name</Label>
                  <Input id="last_name" data-testid="apply-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                  <Input id="email" data-testid="apply-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">Phone (E.164)</Label>
                  <Input id="phone" data-testid="apply-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+61400000000" className="mt-1.5 h-11 rounded-lg" required />
                </div>
                <div>
                  <Label htmlFor="dob" className="text-sm font-medium">Date of birth</Label>
                  <Input id="dob" data-testid="apply-dob" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="mt-1.5 h-11 rounded-lg" required />
                </div>
                <div>
                  <Label className="text-sm font-medium">Gender</Label>
                  <RadioGroup value={form.gender} onValueChange={(v) => set("gender", v)} className="flex gap-2 mt-1.5">
                    {["male", "female"].map((g) => (
                      <label key={g} className={`flex-1 cursor-pointer flex items-center justify-center gap-2 h-11 rounded-lg border text-sm capitalize ${form.gender === g ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5 text-[hsl(var(--accent))] font-medium" : "border-border hover:bg-[hsl(var(--secondary))]"}`}>
                        <RadioGroupItem value={g} className="sr-only" data-testid={`apply-gender-${g}`} /> {g}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </Card>

            <Card className="p-5 sm:p-7 rounded-2xl">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">Membership type</div>
              <RadioGroup value={form.membership_type} onValueChange={(v) => set("membership_type", v)} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { v: "Player", t: "Player", d: "Train and play matches." },
                  { v: "Social", t: "Social", d: "Be part of club life." },
                  { v: "Supporter", t: "Supporter", d: "Support without playing." },
                ].map((m) => {
                  const active = form.membership_type === m.v;
                  return (
                    <label key={m.v} className={`cursor-pointer rounded-xl border-2 p-4 sm:p-5 transition-all relative ${active ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5" : "border-border hover:border-foreground/30"}`}>
                      {active && <span className="absolute top-3 right-3 h-5 w-5 rounded-full bg-[hsl(var(--accent))] text-white flex items-center justify-center"><Check className="h-3 w-3" /></span>}
                      <div className="font-semibold tracking-tight">{m.t}</div>
                      <div className="text-xs text-muted-foreground mt-1">{m.d}</div>
                      <RadioGroupItem value={m.v} className="sr-only" data-testid={`apply-type-${m.v.toLowerCase()}`} />
                    </label>
                  );
                })}
              </RadioGroup>
            </Card>

            <div className="flex items-center justify-between pt-2">
              <Button type="button" variant="outline" className="h-11 px-5 rounded-full" onClick={() => nav("/")}>Cancel</Button>
              <Button type="submit" disabled={busy} className="h-11 px-6 btn-primary-modern" data-testid="apply-submit">
                {busy ? "Submitting…" : <>Submit application <ArrowUpRight className="ml-1.5 h-4 w-4" /></>}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
}
